const SHEET_SELECTOR = 'dialog.ecko-sheet';
const OPEN_CLASS = 'is-open';
const CLOSING_CLASS = 'is-closing';
const DRAGGING_CLASS = 'is-dragging';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const MOBILE_SHEET_QUERY = '(pointer: coarse), (max-width: 799px)';
const BACKDROP_OPACITY = 0.4;
const BACKDROP_BLUR = 2;

const sheetState = new WeakMap();
let initialized = false;
let activeDrag = null;

function getState(dialog) {
	if (!sheetState.has(dialog)) {
		sheetState.set(dialog, {
			closeTimer: null,
			closeDone: null,
			suppressClickUntil: 0
		});
	}

	return sheetState.get(dialog);
}

function supportsModal(dialog) {
	return typeof dialog.showModal === 'function';
}

function isDismissable(dialog) {
	return dialog?.dataset.dismissable !== 'false';
}

function hasReducedMotion() {
	return window.matchMedia?.(REDUCED_MOTION_QUERY).matches;
}

function hasMobileSheetLayout() {
	return window.matchMedia?.(MOBILE_SHEET_QUERY).matches;
}

function isInteractiveTarget(target) {
	return Boolean(target?.closest?.([
		'a',
		'button',
		'input',
		'select',
		'textarea',
		'label',
		'summary',
		'[role="button"]',
		'[data-no-sheet-drag]'
	].join(',')));
}

function getVerticalScrollOwner(target, boundary) {
	let element = target instanceof Element ? target : null;

	while (element && element !== boundary) {
		const styles = window.getComputedStyle(element);
		const canScrollY = /(auto|scroll|overlay)/.test(styles.overflowY);

		if (canScrollY && element.scrollHeight > element.clientHeight + 1) {
			return element;
		}

		element = element.parentElement;
	}

	return null;
}
function parseTimeValue(value) {
	const trimmed = value.trim();
	if (!trimmed) return 0;

	if (trimmed.endsWith('ms')) {
		return Number.parseFloat(trimmed) || 0;
	}

	if (trimmed.endsWith('s')) {
		return (Number.parseFloat(trimmed) || 0) * 1000;
	}

	return Number.parseFloat(trimmed) || 0;
}

function getMaxTransitionMs(element) {
	if (!element) return 0;

	const styles = window.getComputedStyle(element);
	const durations = styles.transitionDuration.split(',').map(parseTimeValue);
	const delays = styles.transitionDelay.split(',').map(parseTimeValue);
	const count = Math.max(durations.length, delays.length);
	let max = 0;

	for (let i = 0; i < count; i += 1) {
		const duration = durations[i] ?? durations[durations.length - 1] ?? 0;
		const delay = delays[i] ?? delays[delays.length - 1] ?? 0;
		max = Math.max(max, duration + delay);
	}

	return max;
}

function clearCloseTimer(dialog) {
	const state = getState(dialog);

	if (state.closeTimer) {
		window.clearTimeout(state.closeTimer);
		state.closeTimer = null;
	}

	state.closeDone = null;
}

function clearDragStyles(dialog) {
	dialog.style.removeProperty('--ecko-sheet-drag-y');
	dialog.style.removeProperty('--ecko-sheet-backdrop-opacity');
	dialog.style.removeProperty('--ecko-sheet-backdrop-blur');
}

function resetSheetState(dialog) {
	clearCloseTimer(dialog);
	dialog.classList.remove(OPEN_CLASS, CLOSING_CLASS, DRAGGING_CLASS);
	clearDragStyles(dialog);
}

function finishClose(dialog, returnValue = '') {
	if (activeDrag?.dialog === dialog) {
		activeDrag = null;
	}

	resetSheetState(dialog);

	if (dialog.open && typeof dialog.close === 'function') {
		dialog.close(returnValue);
	} else {
		dialog.removeAttribute('open');
	}
}

function waitForCloseTransition(dialog, returnValue) {
	const state = getState(dialog);
	const surface = dialog.querySelector('.ecko-sheet__surface');
	const waitTarget = surface || dialog;
	const duration = Math.max(getMaxTransitionMs(waitTarget), getMaxTransitionMs(dialog));
	let finished = false;

	const done = () => {
		if (finished) return;
		finished = true;
		waitTarget.removeEventListener('transitionend', onTransitionEnd);
		state.closeTimer = null;
		state.closeDone = null;
		finishClose(dialog, returnValue);
	};

	const onTransitionEnd = event => {
		if (event.target === waitTarget) {
			done();
		}
	};

	state.closeDone = done;
	waitTarget.addEventListener('transitionend', onTransitionEnd);
	state.closeTimer = window.setTimeout(done, Math.max(duration + 80, 80));
}

function prepareSheet(dialog) {
	if (!dialog || sheetState.has(dialog)) return;

	getState(dialog);

	dialog.addEventListener('cancel', event => {
		event.preventDefault();
		requestCloseSheet(dialog, 'cancel');
	});

	dialog.addEventListener('close', () => {
		resetSheetState(dialog);
	});

	dialog.addEventListener('click', event => {
		const state = getState(dialog);
		if (Date.now() < state.suppressClickUntil) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		const surface = dialog.querySelector('.ecko-sheet__surface');
		if (!surface || surface.contains(event.target)) return;

		requestCloseSheet(dialog, 'backdrop');
	});

	const surface = dialog.querySelector('.ecko-sheet__surface');
	if (surface) {
		surface.addEventListener('pointerdown', event => beginPointerTracking(event, dialog, surface));
		surface.addEventListener('touchstart', event => beginTouchTracking(event, dialog, surface), { passive: true });
	}
}

function canTrackDismissGesture(dialog) {
	return dialog.open && isDismissable(dialog) && !hasReducedMotion() && hasMobileSheetLayout();
}

function createDragState(dialog, surface, startTarget, clientX, clientY, startTime, extras = {}) {
	const content = dialog.querySelector('.ecko-sheet__content');
	const scrollOwner = getVerticalScrollOwner(startTarget, surface) || content;

	return {
		dialog,
		surface,
		content,
		scrollOwner,
		startScrollTop: scrollOwner?.scrollTop || 0,
		startTarget,
		startX: clientX,
		startY: clientY,
		lastMovementY: 0,
		lastTime: startTime,
		velocityY: 0,
		startTime,
		startedOnInteractive: isInteractiveTarget(startTarget),
		isDragging: false,
		...extras
	};
}

function beginPointerTracking(event, dialog, surface) {
	if (!canTrackDismissGesture(dialog)) return;
	if (activeDrag || event.pointerType === 'touch') return;
	if (event.button !== undefined && event.button !== 0) return;
	if (event.pointerType === 'mouse') return;

	activeDrag = createDragState(
		dialog,
		surface,
		event.target,
		event.clientX,
		event.clientY,
		event.timeStamp || performance.now(),
		{ pointerId: event.pointerId }
	);

	window.addEventListener('pointermove', handlePointerMove, { passive: false });
	window.addEventListener('pointerup', handlePointerEnd, { passive: false });
	window.addEventListener('pointercancel', handlePointerCancel, { passive: false });
}

function beginTouchTracking(event, dialog, surface) {
	if (!canTrackDismissGesture(dialog) || activeDrag || event.touches.length !== 1) return;

	const touch = event.touches[0];
	activeDrag = createDragState(
		dialog,
		surface,
		event.target,
		touch.clientX,
		touch.clientY,
		event.timeStamp || performance.now(),
		{ touchId: touch.identifier }
	);

	window.addEventListener('touchmove', handleTouchMove, { passive: false });
	window.addEventListener('touchend', handleTouchEnd, { passive: false });
	window.addEventListener('touchcancel', handleTouchCancel, { passive: false });
}

function getTrackedTouch(touchList, touchId) {
	return Array.from(touchList).find(touch => touch.identifier === touchId) || null;
}

function shouldStartDrag(drag, movementX, movementY) {
	if (drag.startScrollTop > 0) return false;
	if (movementY <= 8) return false;
	if (Math.abs(movementX) > Math.abs(movementY)) return false;
	if (drag.startedOnInteractive && movementY < 24) return false;

	return true;
}

function resistedMovementY(movementY) {
	if (movementY >= 0) return movementY;

	return Math.max(movementY * 0.25, -32);
}

function setDragProgress(dialog, surface, movementY) {
	const sheetHeight = Math.max(surface.getBoundingClientRect().height, 1);
	const y = resistedMovementY(movementY);
	const progress = Math.min(Math.max(y / (sheetHeight * 0.9), 0), 0.85);

	dialog.style.setProperty('--ecko-sheet-drag-y', `${y}px`);
	dialog.style.setProperty('--ecko-sheet-backdrop-opacity', String(BACKDROP_OPACITY * (1 - progress)));
	dialog.style.setProperty('--ecko-sheet-backdrop-blur', String(BACKDROP_BLUR * (1 - progress)));
}

function updateDrag(drag, clientX, clientY, timeStamp, event) {
	const movementX = clientX - drag.startX;
	const movementY = clientY - drag.startY;
	const now = timeStamp || performance.now();
	const timeDelta = Math.max(now - drag.lastTime, 1);

	if (!drag.isDragging) {
		if (!shouldStartDrag(drag, movementX, movementY)) return false;

		drag.isDragging = true;
		drag.dialog.classList.add(DRAGGING_CLASS);
		drag.surface.setPointerCapture?.(drag.pointerId);
	}

	event.preventDefault();

	drag.velocityY = (movementY - drag.lastMovementY) / timeDelta;
	drag.lastMovementY = movementY;
	drag.lastTime = now;

	setDragProgress(drag.dialog, drag.surface, movementY);
	return true;
}

function handlePointerMove(event) {
	const drag = activeDrag;
	if (!drag || event.pointerId !== drag.pointerId) return;

	updateDrag(drag, event.clientX, event.clientY, event.timeStamp, event);
}

function handleTouchMove(event) {
	const drag = activeDrag;
	if (!drag || drag.touchId === undefined) return;

	const touch = getTrackedTouch(event.touches, drag.touchId);
	if (!touch) return;

	updateDrag(drag, touch.clientX, touch.clientY, event.timeStamp, event);
}

function shouldDismissFromDrag(drag, clientY, timeStamp) {
	const movementY = clientY - drag.startY;
	const elapsed = (timeStamp || performance.now()) - drag.startTime;
	const sheetHeight = Math.max(drag.surface.getBoundingClientRect().height, 1);
	const distanceThreshold = Math.min(sheetHeight * 0.25, 120);
	const distanceDismiss = movementY >= distanceThreshold;
	const flickDismiss = movementY >= 50 && drag.velocityY >= 0.5 && elapsed <= 300;

	return distanceDismiss || flickDismiss;
}

function cleanupPointerTracking(drag) {
	window.removeEventListener('pointermove', handlePointerMove);
	window.removeEventListener('pointerup', handlePointerEnd);
	window.removeEventListener('pointercancel', handlePointerCancel);
	drag.surface.releasePointerCapture?.(drag.pointerId);
}

function cleanupTouchTracking() {
	window.removeEventListener('touchmove', handleTouchMove);
	window.removeEventListener('touchend', handleTouchEnd);
	window.removeEventListener('touchcancel', handleTouchCancel);
}

function snapDragBack(drag) {
	const dialog = drag.dialog;

	dialog.classList.remove(DRAGGING_CLASS);
	dialog.style.setProperty('--ecko-sheet-drag-y', '0px');
	dialog.style.setProperty('--ecko-sheet-backdrop-opacity', String(BACKDROP_OPACITY));
	dialog.style.setProperty('--ecko-sheet-backdrop-blur', String(BACKDROP_BLUR));

	window.setTimeout(() => {
		if (!dialog.classList.contains(DRAGGING_CLASS) && dialog.classList.contains(OPEN_CLASS)) {
			clearDragStyles(dialog);
		}
	}, Math.max(getMaxTransitionMs(drag.surface) + 80, 160));
}

function finishDrag(drag, clientY, timeStamp, event) {
	if (!drag.isDragging) {
		activeDrag = null;
		return;
	}

	event.preventDefault();
	getState(drag.dialog).suppressClickUntil = Date.now() + 350;

	if (shouldDismissFromDrag(drag, clientY, timeStamp)) {
		drag.dialog.classList.remove(DRAGGING_CLASS);
		closeSheet(drag.dialog);
	} else {
		snapDragBack(drag);
	}

	activeDrag = null;
}

function handlePointerEnd(event) {
	const drag = activeDrag;
	if (!drag || event.pointerId !== drag.pointerId) return;

	cleanupPointerTracking(drag);
	finishDrag(drag, event.clientY, event.timeStamp, event);
}

function handleTouchEnd(event) {
	const drag = activeDrag;
	if (!drag || drag.touchId === undefined) return;

	const touch = getTrackedTouch(event.changedTouches, drag.touchId);
	cleanupTouchTracking();
	finishDrag(drag, touch?.clientY ?? drag.startY + drag.lastMovementY, event.timeStamp, event);
}

function handlePointerCancel(event) {
	const drag = activeDrag;
	if (!drag || event.pointerId !== drag.pointerId) return;

	cleanupPointerTracking(drag);

	if (drag.isDragging) {
		snapDragBack(drag);
	}

	activeDrag = null;
}

function handleTouchCancel(event) {
	const drag = activeDrag;
	if (!drag || drag.touchId === undefined) return;

	cleanupTouchTracking();

	if (drag.isDragging) {
		snapDragBack(drag);
	}

	activeDrag = null;
}

function handleDocumentCloseClick(event) {
	const closeButton = event.target.closest('[data-close-modal]');
	if (!closeButton) return;

	const dialog = closeButton.closest(SHEET_SELECTOR);
	if (!dialog) return;

	event.preventDefault();
	requestCloseSheet(dialog, 'close-button');
}

export function openSheet(dialog) {
	if (!dialog) return;

	prepareSheet(dialog);
	clearCloseTimer(dialog);
	clearDragStyles(dialog);
	dialog.classList.remove(OPEN_CLASS, CLOSING_CLASS, DRAGGING_CLASS);

	if (!dialog.open) {
		if (supportsModal(dialog)) {
			dialog.showModal();
		} else {
			dialog.setAttribute('open', '');
		}
	}

	dialog.getBoundingClientRect();
	window.requestAnimationFrame(() => {
		window.requestAnimationFrame(() => {
			dialog.classList.add(OPEN_CLASS);
			dialog.style.setProperty('--ecko-sheet-backdrop-opacity', String(BACKDROP_OPACITY));
			dialog.style.setProperty('--ecko-sheet-backdrop-blur', String(BACKDROP_BLUR));
		});
	});
}

export function closeSheet(dialog, options = {}) {
	if (!dialog) return;

	prepareSheet(dialog);

	if (!options.force && !isDismissable(dialog)) {
		return;
	}

	if (!dialog.open) {
		resetSheetState(dialog);
		return;
	}

	if (hasReducedMotion() || options.immediate) {
		finishClose(dialog, options.returnValue || '');
		return;
	}

	const state = getState(dialog);
	if (state.closeDone) {
		state.closeDone();
		return;
	}

	dialog.classList.remove(OPEN_CLASS, DRAGGING_CLASS);
	dialog.classList.add(CLOSING_CLASS);
	waitForCloseTransition(dialog, options.returnValue || '');
}

export function requestCloseSheet(dialog, reason = 'dismiss') {
	if (!dialog || !isDismissable(dialog)) return;

	closeSheet(dialog, { reason });
}

export function initEckoSheets() {
	if (initialized) return;

	initialized = true;
	document.addEventListener('click', handleDocumentCloseClick);

	const setup = () => {
		document.querySelectorAll(SHEET_SELECTOR).forEach(prepareSheet);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', setup);
	} else {
		setup();
	}
}